import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { useFormik } from "formik";
import * as yup from "yup";
import { useCallback, useEffect, useRef, useState } from "react";
import Loading from "../../../../../components/loading/loading";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import { formElementTypes } from "../../../../../utils/Constant";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SubQuestions from "./sub-questions";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import update from "immutability-helper";
import {
  addFormSectionElementRequest,
  deleteFormSectionElementRequest,
  updateFormSectionElementRequest,
} from "../../../../../utils/APICalls";
import ConfigureTable from "./configure-table/configure-table";
import ConfigureMultiInput from "./configure-multi-input/configure-multi-input";
import { type RichTextEditorRef } from "mui-tiptap";
import TextEditor from "../../../../../components/text-editor/text-editor";

interface FormValues {
  isActive: boolean;
  type: string;
  title: string;
  text: string;
  pdfExportText: string;
  answerOptions: string;
  includeOtherOption: boolean;
  definedName: string;
  responseRequired: boolean;
}

export default function ElementSettings({
  data,
  section,
  templateId,
  isClient,
  definedNames,
  selectedDefinedNames,
  onSave,
  onCancel,
  onDelete,
}: any) {
  const rteRef = useRef<RichTextEditorRef>(null);
  const [subQuestions, setSubQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showConfigureTable, setShowConfigureTable] = useState(false);
  const [tableChildElements, setTableChildElements] = useState<any[]>([]);
  const [showConfigureMultiInput, setShowConfigureMultiInput] = useState(false);
  const [multiInputChildElements, setMultiInputChildElements] = useState<any[]>(
    []
  );
  const [htmlText, setHTMLText] = useState();

  useEffect(() => {
    if (data) {
      if (data?.type === formElementTypes.textField) {
        setHTMLText(data?.settings?.text);
      }
      if (data?.type === formElementTypes.matrix) {
        setSubQuestions([...data?.childElements]);
      } else {
        setSubQuestions([{ id: null, title: "", definedName: "" }]);
      }
      if (data?.type === formElementTypes.table) {
        setTableChildElements([...data?.childElements]);
      } else {
        setTableChildElements([]);
      }
      if (data?.type === formElementTypes.multiInput) {
        setMultiInputChildElements([...data?.childElements]);
      } else {
        setMultiInputChildElements([]);
      }
    } else {
      setSubQuestions([{ id: null, title: "", definedName: "" }]);
      setTableChildElements([]);
      setMultiInputChildElements([]);
    }
  }, [data]); // eslint-disable-line

  const ValidationSchema = yup.object({
    title: yup.string().when("type", {
      is: (value: string) => value === formElementTypes.textField,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("This field can't be empty"),
    }),
    text: yup.string().when("type", {
      is: (value: string) => value === formElementTypes.textField,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("This field can't be empty"),
    }),
    answerOptions: yup.string().when("type", {
      is: (value: string) =>
        value === formElementTypes.singleSelect ||
        value === formElementTypes.multiSelect ||
        value === formElementTypes.matrix,
      then: (schema) => schema.required("This field can't be empty"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      isActive: data?.isActive ?? true,
      type: data?.type || "",
      title: data?.settings?.title || "",
      text: data?.settings?.text || "",
      pdfExportText: data?.settings?.pdfExportText || "",
      answerOptions: data?.settings?.answerOptions
        ? data?.settings?.answerOptions?.join(", ")
        : "",
      includeOtherOption: data?.settings?.includeOtherOption ?? false,
      definedName: data?.definedName || null,
      responseRequired: data?.responseRequired ?? false,
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      if (
        values?.type === formElementTypes.textField &&
        rteRef.current?.editor?.isEmpty
      ) {
        setShowErrorNotification(true);
        setErrorMessage("Text field can't be empty.");
        return;
      }
      if (
        values.type === formElementTypes.matrix &&
        subQuestions.length === 0
      ) {
        setShowErrorNotification(true);
        setErrorMessage(
          "Please add ateast one sub-question to save this matrix."
        );
        return;
      }

      if (values.type === formElementTypes.matrix && !isSubQuestionsValid()) {
        return;
      }

      if (
        values.type === formElementTypes.table &&
        tableChildElements.length === 0
      ) {
        setShowErrorNotification(true);
        setErrorMessage("Please Configure Table to save this question.");
        return;
      }

      if (
        values.type === formElementTypes.multiInput &&
        multiInputChildElements.length === 0
      ) {
        setShowErrorNotification(true);
        setErrorMessage("Please Configure Multi-Input to save this question.");
        return;
      }

      const payload: any = {};
      payload.type = values.type;
      payload.order = data?.order;
      payload.isActive = values.isActive;
      payload.settings = {
        title: values.title,
        text: values.text,
      };

      if (
        values?.type === formElementTypes.textField &&
        !rteRef.current?.editor?.isEmpty
      ) {
        payload.settings.text = rteRef.current?.editor?.getHTML();
      }

      if (
        values?.type === formElementTypes.openEnd ||
        values?.type === formElementTypes.singleSelect ||
        values?.type === formElementTypes.multiSelect ||
        values?.type === formElementTypes.matrix ||
        values?.type === formElementTypes.singleDate
      ) {
        payload.definedName = values.definedName;
        payload.responseRequired = values.responseRequired;
        payload.settings = {
          ...payload.settings,
          pdfExportText: values.pdfExportText,
        };
      }

      if (
        values?.type === formElementTypes.singleSelect ||
        values?.type === formElementTypes.multiSelect
      ) {
        payload.settings = {
          ...payload.settings,
          answerOptions:
            values.answerOptions &&
            values.answerOptions
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean),
          includeOtherOption: values.includeOtherOption,
        };
      }

      if (values?.type === formElementTypes.matrix) {
        const updatedSubQuestions = JSON.parse(JSON.stringify(subQuestions));
        updatedSubQuestions.forEach((question: any, index: number) => {
          question.order = index + 1;
        });
        payload.childElements = [...updatedSubQuestions];
        payload.settings = {
          ...payload.settings,
          answerOptions:
            values.answerOptions &&
            values.answerOptions
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean),
        };
      }

      if (values?.type === formElementTypes.photoRequest) {
        payload.responseRequired = values.responseRequired;
        payload.settings = {
          ...payload.settings,
          pdfExportText: values.pdfExportText,
        };
      }

      if (values?.type === formElementTypes.table) {
        payload.responseRequired = values.responseRequired;
        payload.childElements = [...tableChildElements];
        payload.settings = {
          ...payload.settings,
          pdfExportText: values.pdfExportText,
        };
      }

      if (values?.type === formElementTypes.multiInput) {
        payload.childElements = [...multiInputChildElements];
        payload.settings = {
          ...payload.settings,
          pdfExportText: values.pdfExportText,
        };
      }

      if (data?.id.includes("element")) {
        addElement(payload);
      } else {
        updateElement(data.id, payload);
      }
    },
  });

  const isSubQuestionsValid = () => {
    let isValid = true;
    subQuestions.forEach((question) => {
      if (!question.title) {
        isValid = false;
      }
    });
    return isValid;
  };

  const onAddSubQuestion = () => {
    setSubQuestions((prev) => [
      ...prev,
      { id: null, title: "", definedName: "" },
    ]);
  };

  const onUpdateSubQuestion = (
    index: number,
    title?: string,
    definedName?: string
  ) => {
    const questions = JSON.parse(JSON.stringify(subQuestions));
    if (title !== null || title !== undefined) {
      questions[index].title = title;
    }
    if (definedName) {
      questions[index].definedName = definedName;
    }
    setSubQuestions(questions);
  };

  const onDeleteSubQuestion = (index: number) => {
    const questions = [...subQuestions];
    questions.splice(index, 1);
    setSubQuestions(questions);
  };

  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number, question: any) => {
      setSubQuestions((prevCards) => {
        return update(prevCards, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, prevCards[dragIndex]],
          ],
        });
      });
    },
    []
  );

  const addElement = async (payload: any) => {
    if (section?.id) {
      setLoading(true);
      const response = await addFormSectionElementRequest(
        templateId,
        section.id,
        payload,
        isClient
      );
      setLoading(false);
      if (response?.status === 201) {
        const element = {
          id: response.data?.id,
          type: response.data?.type,
          order: response.data?.order,
          isActive: response.data?.isActive,
          definedName: response.data?.definedName,
          responseRequired: response.data?.responseRequired,
          childElements: response.data?.childElements,
          settings: {
            title: response.data?.settings?.title,
            text: response.data?.settings?.text,
            pdfExportText: response.data?.settings?.pdfExportText,
            answerOptions: response.data?.settings?.answerOptions,
            includeOtherOption: response.data?.settings?.includeOtherOption,
          },
        };
        onSave(element);
      } else if (response?.status === 400) {
        setShowErrorNotification(true);
        setErrorMessage(response?.data?.detail);
      }
    } else {
      setShowErrorNotification(true);
      setErrorMessage(
        "Please save section before saving elements inside this section."
      );
    }
  };

  const updateElement = async (elementId: string, payload: any) => {
    setLoading(true);
    const response = await updateFormSectionElementRequest(
      templateId,
      section?.id,
      elementId,
      payload,
      isClient
    );
    setLoading(false);
    if (response?.status === 204) {
      const element = {
        id: elementId,
        type: payload?.type,
        order: payload?.order,
        isActive: payload?.isActive,
        definedName: payload?.definedName,
        responseRequired: payload?.responseRequired,
        childElements: payload?.childElements,
        settings: {
          title: payload?.settings?.title,
          text: payload?.settings?.text,
          pdfExportText: payload?.settings?.pdfExportText,
          answerOptions: payload?.settings?.answerOptions,
          includeOtherOption: payload?.settings?.includeOtherOption,
        },
      };
      onSave(element);
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onDeleteClick = async () => {
    if (!data?.id.includes("element")) {
      setLoading(true);
      const response = await deleteFormSectionElementRequest(
        templateId,
        section?.id,
        data?.id,
        isClient
      );
      setLoading(false);
      if (response?.status === 204) {
        onDelete(data);
      } else if (response?.status === 400) {
        setShowErrorNotification(true);
        setErrorMessage(response?.data?.detail);
      }
    } else {
      onDelete(data);
    }
  };

  const onSaveTableChildElements = (childElements: any[]) => {
    setTableChildElements(childElements);
  };

  const onSaveMultiInputChildElements = (childElements: any[]) => {
    setMultiInputChildElements(childElements);
  };

  return (
    <Box sx={{ p: 2 }}>
      <form onSubmit={formik.handleSubmit}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography fontWeight={"bold"} fontSize={20}>
            {data?.type === formElementTypes.textField && "Text Field Settings"}
            {(formik.values.type === formElementTypes.openEnd ||
              formik.values.type === formElementTypes.singleSelect ||
              formik.values.type === formElementTypes.multiSelect ||
              formik.values.type === formElementTypes.matrix ||
              formik.values.type === formElementTypes.table ||
              formik.values.type === formElementTypes.multiInput) &&
              "Question Settings"}
            {data?.type === formElementTypes.singleDate &&
              "Date Picker Settings"}
            {data?.type === formElementTypes.photoRequest &&
              "Photo Request Settings"}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                name="isActive"
                checked={formik.values.isActive}
                onChange={formik.handleChange}
              />
            }
            label={formik.values.isActive ? "Active" : "Inactive"}
          />
        </Box>

        {(data?.type === formElementTypes.openEnd ||
          data?.type === formElementTypes.singleSelect ||
          data?.type === formElementTypes.multiSelect ||
          data?.type === formElementTypes.matrix ||
          data?.type === formElementTypes.table ||
          data?.type === formElementTypes.multiInput) && (
          <Box>
            <Box sx={{ display: "flex", my: 2 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.6)",
                  width: 150,
                }}
              >
                Question Type
              </Typography>
              <FormControl fullWidth sx={{ ml: 2 }}>
                <Select
                  labelId="type-select-label"
                  id="type-select"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                >
                  <MenuItem value={formElementTypes.openEnd}>Open End</MenuItem>
                  <MenuItem value={formElementTypes.singleSelect}>
                    Single Select
                  </MenuItem>
                  <MenuItem value={formElementTypes.multiSelect}>
                    Multi Select
                  </MenuItem>
                  <MenuItem value={formElementTypes.matrix}>Matrix</MenuItem>
                  <MenuItem value={formElementTypes.table}>Table</MenuItem>
                  <MenuItem value={formElementTypes.multiInput}>
                    Multi-Input
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Divider />
          </Box>
        )}

        {data?.type === formElementTypes.singleDate && (
          <Box>
            <Box sx={{ display: "flex", my: 2 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.6)",
                  width: 150,
                }}
              >
                Date Type
              </Typography>
              <FormControl fullWidth sx={{ ml: 2 }}>
                <Select
                  labelId="type-select-label"
                  id="type-select"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                >
                  <MenuItem value={formElementTypes.singleDate}>
                    Single Date
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Divider />
          </Box>
        )}

        <Box sx={{ display: "flex", my: 2 }}>
          <Typography
            sx={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.6)", width: 150 }}
          >
            {(data?.type === formElementTypes.textField ||
              data?.type === formElementTypes.singleDate ||
              data?.type === formElementTypes.photoRequest) &&
              "Title"}
            {(data?.type === formElementTypes.openEnd ||
              data?.type === formElementTypes.singleSelect ||
              data?.type === formElementTypes.multiSelect ||
              data?.type === formElementTypes.matrix ||
              data?.type === formElementTypes.table ||
              data?.type === formElementTypes.multiInput) &&
              "Question Title"}
          </Typography>
          <TextField
            sx={{ ml: 2 }}
            fullWidth
            id="title"
            name="title"
            variant="outlined"
            value={formik.values.title}
            onChange={formik.handleChange}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={
              data?.type === formElementTypes.textField
                ? "Leave blank to exclude."
                : formik.touched.title && formik.errors.title
            }
          />
        </Box>
        <Divider />
        {data?.type === formElementTypes.textField && (
          <>
            <Box sx={{ display: "flex", my: 2 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.6)",
                  width: 150,
                }}
              >
                Text
              </Typography>

              <TextEditor rteRef={rteRef} htmlText={htmlText} />

              {/* <TextField
                sx={{ ml: 2 }}
                fullWidth
                id="text"
                name="text"
                variant="outlined"
                multiline
                rows={5}
                value={formik.values.text}
                onChange={formik.handleChange}
                error={formik.touched.text && Boolean(formik.errors.text)}
                helperText={formik.touched.text && formik.errors.text}
              /> */}
            </Box>
            <Divider />
          </>
        )}
        {(data?.type === formElementTypes.openEnd ||
          data?.type === formElementTypes.singleSelect ||
          data?.type === formElementTypes.multiSelect ||
          data?.type === formElementTypes.matrix ||
          data?.type === formElementTypes.table ||
          data?.type === formElementTypes.singleDate ||
          data?.type === formElementTypes.photoRequest ||
          data?.type === formElementTypes.multiInput) && (
          <>
            <Box sx={{ display: "flex", my: 2 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.6)",
                  width: 150,
                }}
              >
                {data?.type === formElementTypes.photoRequest
                  ? "Instructions"
                  : "Question Text"}
              </Typography>
              <Box sx={{ ml: 2, width: "100%" }}>
                <TextField
                  fullWidth
                  id="inspectorText"
                  name="text"
                  label="For Inspector"
                  variant="outlined"
                  value={formik.values.text}
                  onChange={formik.handleChange}
                  error={formik.touched.text && Boolean(formik.errors.text)}
                  helperText={formik.touched.text && formik.errors.text}
                />
                <TextField
                  sx={{ mt: 2 }}
                  fullWidth
                  id="pdfExportText"
                  name="pdfExportText"
                  label="On Client Report (PDF Exports Only)"
                  variant="outlined"
                  value={formik.values.pdfExportText}
                  onChange={formik.handleChange}
                  helperText="Leave blank if same."
                />
              </Box>
            </Box>
            <Divider />
          </>
        )}

        {formik.values.type === formElementTypes.matrix && (
          <Box>
            <Box sx={{ display: "flex", my: 2 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.6)",
                  width: 150,
                }}
              >
                Sub Questions
              </Typography>
              <Box width={"100%"}>
                {subQuestions.map((question: any, index: number) => (
                  <SubQuestions
                    key={index}
                    question={question}
                    index={index}
                    definedNames={definedNames}
                    usedDefinedNames={selectedDefinedNames}
                    allQuestions={subQuestions}
                    submitCount={formik.submitCount}
                    onDelete={onDeleteSubQuestion}
                    onUpdate={onUpdateSubQuestion}
                    moveCard={moveCard}
                  />
                ))}
                <Button
                  variant="text"
                  startIcon={<AddOutlinedIcon />}
                  onClick={onAddSubQuestion}
                >
                  Add Sub Question
                </Button>
              </Box>
            </Box>
            <Divider />
          </Box>
        )}

        {(formik.values.type === formElementTypes.singleSelect ||
          formik.values.type === formElementTypes.multiSelect ||
          formik.values.type === formElementTypes.matrix) && (
          <>
            <Box sx={{ display: "flex", my: 2 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.6)",
                  width: 150,
                }}
              >
                Answer Options
              </Typography>
              <Box sx={{ width: "100%", ml: 2 }}>
                <TextField
                  fullWidth
                  id="answerOptions"
                  name="answerOptions"
                  variant="outlined"
                  value={formik.values.answerOptions}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.answerOptions &&
                    Boolean(formik.errors.answerOptions)
                  }
                  helperText={
                    formik.touched.answerOptions &&
                    Boolean(formik.errors.answerOptions)
                      ? formik.errors.answerOptions
                      : "Separate by comma."
                  }
                />
                {(formik.values.type === formElementTypes.singleSelect ||
                  formik.values.type === formElementTypes.multiSelect) && (
                  <FormGroup sx={{ mt: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="includeOtherOption"
                          checked={formik.values.includeOtherOption}
                          onChange={formik.handleChange}
                        />
                      }
                      label="Include “Other” option"
                    />
                  </FormGroup>
                )}
              </Box>
            </Box>
            <Divider />
          </>
        )}

        {(formik.values.type === formElementTypes.openEnd ||
          formik.values.type === formElementTypes.singleSelect ||
          formik.values.type === formElementTypes.multiSelect ||
          formik.values.type === formElementTypes.singleDate) && (
          <>
            <Box sx={{ display: "flex", my: 2 }}>
              <Box
                sx={{
                  width: 150,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
                  Defined Name
                </Typography>
                <Tooltip
                  title="The unique defined name of the cell in the excel file where you want this question response to go."
                  arrow
                  placement="top"
                >
                  <IconButton sx={{ p: 0 }}>
                    <InfoOutlinedIcon color="primary" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Autocomplete
                fullWidth
                sx={{ ml: 2 }}
                disablePortal
                options={definedNames}
                value={formik.values.definedName}
                getOptionDisabled={(option) =>
                  selectedDefinedNames.includes(option)
                }
                onChange={(event, newValue: any) => {
                  formik.setFieldValue("definedName", newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    name="definedName"
                    {...params}
                    label="Defined Name"
                    error={
                      formik.touched.definedName &&
                      Boolean(formik.errors.definedName)
                    }
                    helperText={
                      formik.touched.definedName && formik.errors.definedName
                    }
                  />
                )}
              />
            </Box>
            <Divider />
          </>
        )}

        {formik.values.type === formElementTypes.table && (
          <>
            <Box sx={{ display: "flex", my: 2 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.6)",
                  width: 150,
                }}
              >
                Table Configuration
              </Typography>
              <Box sx={{ ml: 2, width: "100%" }}>
                <Button
                  variant="contained"
                  onClick={() => setShowConfigureTable(true)}
                >
                  Configure Table
                </Button>
              </Box>
            </Box>
            <Divider />
          </>
        )}

        {formik.values.type === formElementTypes.multiInput && (
          <>
            <Box sx={{ display: "flex", my: 2 }}>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(0, 0, 0, 0.6)",
                  width: 150,
                }}
              >
                Configuration
              </Typography>
              <Box sx={{ ml: 2, width: "100%" }}>
                <Button
                  variant="contained"
                  onClick={() => setShowConfigureMultiInput(true)}
                >
                  Configure
                </Button>
              </Box>
            </Box>
            <Divider />
          </>
        )}

        {formik.values.type !== formElementTypes.textField &&
          formik.values.type !== formElementTypes.multiInput && (
            <>
              <Box sx={{ display: "flex", my: 2 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "rgba(0, 0, 0, 0.6)",
                    width: 150,
                  }}
                >
                  Additional Options
                </Typography>
                <FormGroup sx={{ ml: 2, width: "100%" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="responseRequired"
                        checked={formik.values.responseRequired}
                        onChange={formik.handleChange}
                      />
                    }
                    label="Response Required?"
                  />
                </FormGroup>
              </Box>
              <Divider />
            </>
          )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            my: 3,
          }}
        >
          <Button
            startIcon={<DeleteOutlinedIcon color="error" />}
            variant="text"
            color="error"
            onClick={onDeleteClick}
          >
            {data?.type === formElementTypes.textField && "Delete Text Field"}
            {(data?.type === formElementTypes.openEnd ||
              data?.type === formElementTypes.singleSelect ||
              data?.type === formElementTypes.multiSelect ||
              data?.type === formElementTypes.matrix ||
              data?.type === formElementTypes.table ||
              data?.type === formElementTypes.singleDate ||
              data?.type === formElementTypes.multiInput) &&
              "Delete Question"}
            {data?.type === formElementTypes.photoRequest &&
              "Delete Photo Request"}
          </Button>
          <Box>
            <Button variant="outlined" sx={{ mr: 2 }} onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => formik.handleSubmit()}>
              Save
            </Button>
          </Box>
        </Box>
      </form>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {showConfigureTable && (
        <ConfigureTable
          definedNames={definedNames}
          usedDefinedNames={selectedDefinedNames}
          tableData={tableChildElements}
          showDialog={showConfigureTable}
          closeDialog={() => setShowConfigureTable(false)}
          onSave={onSaveTableChildElements}
        />
      )}
      {showConfigureMultiInput && (
        <ConfigureMultiInput
          definedNames={definedNames}
          usedDefinedNames={selectedDefinedNames}
          multiInputData={multiInputChildElements}
          showDialog={showConfigureMultiInput}
          closeDialog={() => setShowConfigureMultiInput(false)}
          onSave={onSaveMultiInputChildElements}
        />
      )}
      {loading && <Loading />}
    </Box>
  );
}
